import { useCallback, useState } from 'react';

type ModalState<T> = {
  isOpen: boolean;
  context: T;
};

type ContextParam<T> = { context?: T };

export type ModalStateWithContext<T> = ModalState<T> & {
  onOpen: (params?: ContextParam<T>) => void;
  onClose: (params?: ContextParam<T>) => void;
};

/**
 * Hook for managing modal states when modal context is also required. This will provide an isOpen state,
 * and handlers for isOpen/isClosed. Additionally, it provides context to simplify passing state to modals.
 * This can be updated through the open and close handlers
 * @param {T} initialContext initial context state
 * @returns {ModalReturn<T>} modal state and handlers
 */
const useModalWithContext = <T>(initialContext: T = {} as T): ModalStateWithContext<T> => {
  const [modalState, setModalState] = useState<ModalState<T>>({
    isOpen: false,
    context: initialContext,
  });

  // onOpen is a callback to be returned from the hook
  const onOpen = useCallback(() => (params: ContextParam<T> = {}) => setModalState(prevState => {
    const { context } = params;

    const contextToCommit = context || prevState.context;

    return {
      ...prevState,
      isOpen: true,
      context: contextToCommit,
    };
  }), [setModalState]);

  // onOpen is a callback to be returned from the hook
  const onClose = useCallback(() => (params: ContextParam<T> = {}) => setModalState(prevState => {
    const { context } = params;
    const contextToCommit = context || prevState.context;

    return {
      ...prevState,
      isOpen: false,
      context: contextToCommit,
    };
  }), [setModalState]);

  return {
    ...modalState,
    onOpen: onOpen(),
    onClose: onClose(),
  };
};

export default useModalWithContext;
